import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MemoryChatButtons } from './MemoryChatButtons';
import { useAutosizeTextarea } from '../../../shared/use-autosize-textarea';
import { useSpeechRecognition } from '../../../shared/use-speech-recognition';
import { MEMORY_KEEPER_TOUR_TARGETS } from '../../../../lib/shared/memory-keeper-tour-steps';
import { appendTranscript, getSpeechRecognitionErrorMessage } from '../../../../lib/shared/speech-recognition';
import styles from './MemoryChatComposer.css';

function MemoryChatComposer({ onSend, sendErrorMessage }) {
    const [inputValue, setInputValue] = useState('');

    const handleTranscript = (transcript) => {
        setInputValue((currentValue) => appendTranscript(currentValue, transcript));
    };

    const { isListening, interimTranscript, errorType, startListening, stopListening } = useSpeechRecognition({ onResult: handleTranscript });
    const composedValue = appendTranscript(inputValue, interimTranscript);
    const textareaRef = useAutosizeTextarea(composedValue);
    const voiceErrorMessage = getSpeechRecognitionErrorMessage(errorType);
    const errorMessage = sendErrorMessage || voiceErrorMessage;
    const hasText = Boolean(composedValue.trim());

    const sendMemory = () => {
        const text = composedValue.trim();

        if (!text) {
            return;
        }

        stopListening();
        onSend(text);
        setInputValue('');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        sendMemory();
    };

    const handleChange = (event) => {
        if (isListening) {
            stopListening();
        }

        setInputValue(event.target.value);
    };

    const handleVoiceInputClick = () => {
        if (isListening) {
            stopListening();

            return;
        }

        startListening();
    };

    const handleKeyDown = (event) => {
        if (event.nativeEvent.isComposing) {
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMemory();
        }
    };

    return (
        <form
            className={styles.composer}
            onSubmit={handleSubmit}
            data-tour={MEMORY_KEEPER_TOUR_TARGETS.COMPOSER}
        >
            {Boolean(errorMessage) && <p className={styles.composerError} role='alert'>{errorMessage}</p>}

            <textarea
                id='memory'
                name='memory'
                ref={textareaRef}
                className={styles.composerField}
                value={composedValue}
                placeholder='Write a memory...'
                rows={1}
                autoComplete='off'
                aria-label='Write a memory'
                onChange={handleChange}
                onKeyDown={handleKeyDown}
            />
            <MemoryChatButtons
                isListening={isListening}
                hasText={hasText}
                onVoiceInputClick={handleVoiceInputClick}
            />
        </form>
    );
}

MemoryChatComposer.propTypes = {
    onSend: PropTypes.func.isRequired,
    sendErrorMessage: PropTypes.string
};

MemoryChatComposer.defaultProps = {
    sendErrorMessage: null
};

export {
    MemoryChatComposer
};
